/**
 * Tenant Layout (Web Only)
 *
 * Validates the tenantId in the URL before rendering child routes.
 * This file only runs on web (.web.tsx extension).
 * Mobile uses +native-intent.tsx for deep link validation.
 *
 * Cases:
 * 1. tenantId === selectedTenant.id -> allow navigation
 * 2. tenantId in tenants -> switch tenant silently, allow navigation
 * 3. tenantId NOT in tenants -> redirect to Not Found
 */

import { ActivityIndicator } from "react-native";
import { Redirect, Stack, useLocalSearchParams } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import { useValidateTenantFromUrl, AccountNotConfiguredView, useAuth } from "@src/features/tenants";

export default function TenantLayout() {
  const { theme } = useUnistyles();
  const { isInitialized } = useAuth();
  const params = useLocalSearchParams<{ tenantId: string }>();
  const urlTenantId = params.tenantId;

  const { isValidating, isValid, error, refreshTenants } = useValidateTenantFromUrl(urlTenantId);

  if (!isInitialized || isValidating) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" bg="background">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error) {
    return (
      <AccountNotConfiguredView
        error={error?.message}
        onRetry={() => {
          void refreshTenants();
        }}
      />
    );
  }

  if (!isValid) {
    return <Redirect href="/not-found" />;
  }

  return (
    <Stack
      screenOptions={{
        headerStyle: {
          backgroundColor: theme.colors.background,
        },
        headerTintColor: theme.colors.text,
        headerBackButtonDisplayMode: "minimal",
        contentStyle: {
          backgroundColor: theme.colors.background,
        },
      }}
    />
  );
}
